import React from 'react';
import PropTypes from 'prop-types';

import {Text, Image as BaseImage, Skeleton, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import BaseLink from '@amblique/base/app/components/link';
import {urlHelper} from '@amblique/base/app/helpers/url-helper';

/**
 * @override
 * Link component from Contentful
 * @param {Object} props - Component props
 * @param {Contentful.Link} props.component - Link component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {React.ReactNode} [props.children] - Additional content to render inside link
 * @returns {React.ReactElement}
 */
export const Link = ({component, isComplete = true, isFetching = false, ...props}) => {
  const {text, url, openInNewTab, type, image, helptext, iconPosition} = component;
  const styles = useMultiStyleConfig('contentful/link', {component, type, iconPosition});

  // Show skeleton while fetching
  if (!isComplete && isFetching) {
    return (
      <Skeleton
        height="48px"
        width="90px"
      />
    );
  }

  // Determine if URL is internal or external
  const isExternal = url && (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//'));
  // Hash-only URLs (like #calendly-in-person) should not be processed
  const isHashOnly = url && url.startsWith('#');
  const parsedUrl = !isExternal && !isHashOnly && url ? urlHelper.parseStorefrontUrl(url) : null;
  const linkUrl = parsedUrl?.appUrl || url;

  // Determine link variant based on type
  const getVariant = () => {
    switch (type) {
      case 'Primary':
        return 'filled-light';
      case 'Secondary':
        return 'outlined-light';
      case 'Filled Dark':
        return 'filled-dark';
      case 'Outlined Dark':
        return 'outlined-dark';
      case 'Card':
      default:
        return 'base'; // Default text link
    }
  };

  const variant = getVariant();
  const hasImage = Boolean(image?.url);

  // Normalize iconPosition to handle case-insensitive and whitespace
  const normalizedIconPosition = iconPosition?.toString().toLowerCase().trim();
  const isValidIconPosition = normalizedIconPosition === 'left' || normalizedIconPosition === 'right';

  // Determine display logic: if iconPosition exists and image is present, display both image and text
  const shouldDisplayBoth = isValidIconPosition && hasImage;
  const shouldDisplayImageOnly = hasImage && !shouldDisplayBoth;

  // Hash-only URLs should use href (like external links) to prevent React Router processing
  const useHref = isExternal || isHashOnly;

  const linkProps = {
    href: useHref ? linkUrl : undefined,
    to: !useHref ? linkUrl : undefined,
    target: openInNewTab ? '_blank' : undefined,
    rel: openInNewTab ? 'noopener noreferrer' : undefined,
    title: helptext || text,
    variant,
    ...styles.root,
  };

  const linkClassName = `link link--${variant} ${shouldDisplayBoth ? 'link--icon-on-' + normalizedIconPosition : ''}`;

  // Render content based on display mode
  const renderContent = () => {
    // Custom children override everything
    if (props.children) {
      return props.children;
    }

    // Case 1: Display both image and text (when iconPosition is set)
    if (shouldDisplayBoth) {
      return (
        <>
          {normalizedIconPosition === 'right' ? (
            <>
              <Text {...styles.text}>{text}</Text>
              <span {...styles.imageWrapper}>
                <BaseImage
                  {...styles.image}
                  alt={image.title || text || ''}
                  src={image.url}
                />
              </span>
            </>
          ) : (
            <>
              <span {...styles.imageWrapper}>
                <BaseImage
                  {...styles.image}
                  alt={image.title || text || ''}
                  src={image.url}
                />
              </span>
              <Text {...styles.text}>{text}</Text>
            </>
          )}
        </>
      );
    }

    // Case 2: Display image only
    if (shouldDisplayImageOnly) {
      return (
        <BaseImage
          {...styles.image}
          alt={image.title || text || ''}
          src={image.url}
        />
      );
    }

    // Case 3: Display text only (always available since link always has text)
    return (
      <Text {...styles.text}>{text}</Text>
    );
  };

  return (
    <BaseLink
      {...linkProps}
      className={linkClassName}
    >
      {renderContent()}
    </BaseLink>
  );
};

Link.displayName = 'Link';
Link.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
